import React, {useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import usePermissions from "../../hooks/usePermissions";
import {RestDayModal} from "../../calander/CalendarModals";
import {ALL_WEEKDAYS, CALENDAR_HOURS_KEY, REST_DAYS_KEY, REST_FULL_DAY, saveRestSlots} from "../../calander/restDays";
import {
    apiErrorMessage,
    createRestDays,
    deleteRestDay,
    fetchCalendarHours,
    fetchRestDays,
    updateRestDay,
} from "../Setting/Calendar/calendarSettingsApi";

// Repos / Congé and opening hours of the calendar, under its query keys: an edit
// here, in the calendar or in Réglages refreshes all three screens. `ready` stays
// false until both are loaded, because a day can't be called an absence before.
export const useRestCalendar = (from) => {
    const rest = useQuery({
        queryKey: [...REST_DAYS_KEY, from],
        queryFn: () => fetchRestDays({from}),
        staleTime: 60 * 1000,
    });
    const hours = useQuery({
        queryKey: CALENDAR_HOURS_KEY,
        queryFn: fetchCalendarHours,
        staleTime: 5 * 60 * 1000,
    });
    return {
        restDays: rest.data?.restDays || [],
        resources: rest.data?.employees || [],
        hours: hours.data ?? null,
        ready: rest.isSuccess && hours.isSuccess,
        isError: rest.isError || hours.isError,
    };
};

// Declare or edit a Repos / Congé from the attendance screen: the calendar's own
// form, saved the same way (sent to the tablets, blocks bookings on those days).
export const useRestEditor = (resources) => {
    const {canUpdate} = usePermissions();
    const queryClient = useQueryClient();
    const [modal, setModal] = useState(null); // {formKey, id?, initial}
    const [saving, setSaving] = useState(false);

    const refresh = () => queryClient.invalidateQueries({queryKey: REST_DAYS_KEY});

    const openLeave = (employeeId, date) => setModal({
        formKey: Date.now(),
        initial: {
            employee_ids: employeeId ? [employeeId] : [],
            slots: [{days_of_week: ALL_WEEKDAYS, ...REST_FULL_DAY}],
            start_date: date,
            end_date: date,
            recurring: false,
        },
    });

    const openEdit = (rest) => setModal({
        formKey: Date.now(),
        id: rest.id,
        initial: {
            employee_ids: rest.employee_id ? [rest.employee_id] : [],
            slots: [{days_of_week: rest.days_of_week, start_time: rest.start_time, end_time: rest.end_time}],
            start_date: rest.start_date,
            end_date: rest.end_date,
            recurring: rest.recurring,
        },
    });

    const save = async (form) => {
        setSaving(true);
        const {saved, total, error} = await saveRestSlots(form, {id: modal.id, create: createRestDays, update: updateRestDay});
        setSaving(false);
        if (saved > 0) refresh();

        if (!error) {
            const kind = form.slots[0].days_of_week.length === 7 ? "Congé" : "Repos";
            toast.success(total > 1
                ? `${total} créneaux de repos enregistrés — envoyés aux caisses`
                : `${kind} ${modal.id ? "modifié" : "ajouté"} — envoyé aux caisses`);
            setModal(null);
            return;
        }
        const message = apiErrorMessage(error, "Impossible d'enregistrer");
        toast.error(saved > 0 ? `${saved} créneau(x) sur ${total} enregistré(s). ${message}` : message);
        // Keep only what's left to save: the saved slots must not be sent twice on retry
        if (saved > 0) setModal({formKey: Date.now(), initial: {...form, slots: form.slots.slice(saved)}});
    };

    const remove = async () => {
        setSaving(true);
        try {
            await deleteRestDay(modal.id);
            toast.success("Supprimé — envoyé aux caisses");
            setModal(null);
            refresh();
        } catch (error) {
            toast.error(apiErrorMessage(error, "Impossible de supprimer"));
        } finally {
            setSaving(false);
        }
    };

    return {
        canManage: canUpdate("calendar") || canUpdate("settings"),
        openLeave,
        openEdit,
        element: modal && (
            <RestDayModal
                key={modal.formKey}
                initial={modal.initial}
                resources={resources}
                editing={!!modal.id}
                saving={saving}
                onSave={save}
                onDelete={modal.id ? remove : undefined}
                onClose={() => setModal(null)}
            />
        ),
    };
};
